<template>
  <div v-if="menuData" class="nav_submenu" :class="{ active: isVisible }" :data-menu-id="menuData.id">
    <div class="submenu_left_space"></div>

    <!-- Left navigation -->
    <div class="submenu_left">
      <component
        :is="(child.hasContent && false) ? 'a' : 'span'"
        v-for="(child, index) in menuData.children"
        :key="child.id"
        :href="child.hasContent ? child.link : undefined"
        :data-pageid="child.id"
        :class="['submenu_link', {
          'active': index === activeChildIndex || child.isCurrent || child.isParent,
          'disabled': !child.hasContent
        }]"
      >
        <span class="submenu_link_text" @mouseenter="activeChildIndex = index">{{ child.title }}</span>
      </component>
    </div>

    <!-- Center content (ProductSubmenu or PageSubmenu) -->
    <div
      v-for="(child, index) in menuData.children"
      :key="`content-${child.id}`"
      class="submenu_center"
      :class="{ active: index === activeChildIndex }"
      :data-pageid="child.id"
    >
      <!-- ProductSubmenu template (for ProductBasePage) -->
      <template v-if="menuData.isProductPage && menuData.menus">
        <div class="submenu_center_main">
          <h3 class="submenu_center_title">
            <component :is="menuData.hasContent ? 'a' : 'span'" :href="menuData.hasContent ? menuData.link : undefined" :class="{ 'disabled': !menuData.hasContent }">
              {{ menuData.title }}
            </component>
          </h3>
          <p v-if="menuData.summary" class="submenu_center_summary">{{ menuData.summary }}</p>

          <ul class="submenu_center_menu">
            <li v-for="menu in menuData.menus" :key="menu.title">
              <a class="hl4 submenu_center_menutitle" :href="menu.link">{{ menu.title }}</a>
              <ul class="submenu_center_submenu">
                <li v-for="link in menu.links" :key="link.url">
                  <a :href="link.url">{{ link.title }}</a>
                </li>
              </ul>
            </li>
          </ul>

          <div v-if="menuData.menuLinks && menuData.menuLinks.length" class="submenu_center_menulinks">
            <a
              v-for="menuLink in menuData.menuLinks"
              :key="menuLink.url"
              :href="menuLink.url"
              class="submenu_center_button link--button link--button--text"
            >
              {{ menuLink.title }}
            </a>
          </div>
        </div>
      </template>

      <!-- PageSubmenu template (for regular pages) -->
      <template v-else>
        <div class="submenu_center_main">
          <h3 class="submenu_center_title">
            <component :is="(child.hasContent && false) ? 'a' : 'span'" :href="child.hasContent ? child.link : undefined" :class="{ 'disabled': !child.hasContent }">
              {{ child.title }}
            </component>
          </h3>
          <p v-if="child.summary" class="submenu_center_summary">{{ child.summary }}</p>

          <!-- If child is ProductBasePage, show menus -->
          <ul v-if="child.isProductPage && child.menus && child.menus.length" class="submenu_center_menu">
            <li v-for="menu in child.menus" :key="menu.title">
              <a class="hl4 submenu_center_menutitle" :href="menu.link">{{ menu.title }}</a>
              <ul class="submenu_center_submenu">
                <li v-for="link in menu.links" :key="link.url">
                  <a :href="link.url">{{ link.title }}</a>
                </li>
              </ul>
            </li>
          </ul>

          <!-- Otherwise show children as normal -->
          <ul v-else-if="child.children && child.children.length" class="submenu_center_menu">
            <li v-for="subchild in child.children" :key="subchild.id">
              <component :is="subchild.hasContent ? 'a' : 'span'" class="hl4 submenu_center_menutitle" :href="subchild.hasContent ? subchild.link : undefined" :class="{ 'disabled': !subchild.hasContent }">
                {{ subchild.title }}
              </component>
              <ul v-if="subchild.children && subchild.children.length" class="submenu_center_submenu">
                <li v-for="grandchild in subchild.children" :key="grandchild.id">
                  <component :is="grandchild.hasContent ? 'a' : 'span'" :href="grandchild.hasContent ? grandchild.link : undefined" :class="{ 'disabled': !grandchild.hasContent }">
                    {{ grandchild.title }}
                  </component>
                </li>
              </ul>
            </li>
          </ul>

          <!-- Show button whenever the page has content, even if it has children -->
          <div v-if="child.hasContent" class="submenu_center_menulinks">
            <a
              :href="child.link"
              class="link--button link--button--secondary"
            >
              {{ translations.goToPage }} "{{ child.title }}"
            </a>
          </div>

          <div v-if="child.menuLinks && child.menuLinks.length" class="submenu_center_menulinks">
            <a
              v-for="menuLink in child.menuLinks"
              :key="menuLink.url"
              :href="menuLink.url"
              class="submenu_center_buttonx link--button link--button--primary"
            >
              {{ menuLink.title }}
            </a>
          </div>
        </div>
      </template>
    </div>

    <div class="submenu_right_space"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
  menuId: {
    type: Number,
    required: true
  },
  currentUrl: {
    type: String,
    default: ''
  },
  preloadedData: {
    type: Object,
    default: null
  }
});

const menuData = ref(null);
const activeChildIndex = ref(0);
const loading = ref(false);
const error = ref(null);
const isVisible = ref(false);
const translations = ref({ goToPage: 'Go to page' }); // Default fallback

// Load menu data from preloaded data (API fallback removed - data is always preloaded)
const loadMenuData = () => {
  if (!props.menuId) return;

  // Use preloaded data (required - no API fallback)
  if (!props.preloadedData) {
    console.warn('NavigationSubmenu: No preloaded data provided for menuId', props.menuId);
    return;
  }

  // Extract translations if present (new format), otherwise use default
  if (props.preloadedData.translations) {
    translations.value = props.preloadedData.translations;
  }

  // Set menu data (exclude translations property)
  const { translations: _, ...menuDataOnly } = props.preloadedData;
  menuData.value = menuDataOnly;

  if (menuData.value) {
    // Find the active child index based on isCurrent or isParent
    const activeIndex = menuData.value.children.findIndex(child => child.isCurrent || child.isParent);
    if (activeIndex !== -1) {
      activeChildIndex.value = activeIndex;
    }

    // Show submenu immediately
    setTimeout(() => {
      isVisible.value = true;
    }, 10);
  }
};

// Method to hide submenu (called from parent)
const hide = () => {
  isVisible.value = false;
};

// Expose hide method for parent component
defineExpose({
  hide
});

// Load data on mount
onMounted(() => {
  loadMenuData();
});

// Reload if menuId changes
watch(() => props.menuId, () => {
  loadMenuData();
});
</script>
